import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

// Constants
const WEBSITE_NAME = 'NINEEYE';

// Create axios instance
// Use environment variable for API URL, fallback to production API or localhost for development
const getApiBaseURL = (): string => {
  const env = import.meta.env;
  
  // Check for explicit API base URL override
  if (env.VITE_API_BASE_URL) {
    return env.VITE_API_BASE_URL;
  }
  
  // Production: use api.nineeyeshop.com, Development: use localhost
  if (env.PROD) {
    return 'https://api.nineeyeshop.com/api';
  }
  
  return 'https://api.nineeyeshop.com/api';
};

const api = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const { token } = useAuthStore.getState();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      useAuthStore.getState().clearAuth();
      toast.error('Session expired. Please login again.');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      toast.error('Access denied');
    } else if (error.response?.status === 404) {
      toast.error('Resource not found');
    } else if (error.response?.status === 429) {
      toast.error('Too many requests. Please try again later.');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please check your connection.');
    } else if (!error.response) {
      toast.error('Network error. Please check your connection.');
    } else {
      const message = error.response?.data?.message || 'An error occurred';
      toast.error(message);
    }
    
    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  register: async (email: string, password: string, firstName: string, lastName: string) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      website: WEBSITE_NAME,
      firstName,
      lastName
    });
    return response.data;
  },

  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', {
      email,
      password,
      website: WEBSITE_NAME
    });
    return response.data;
  },

  refreshToken: async (refreshToken: string) => {
    const response = await api.post('/auth/refresh', { refreshToken });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
};

// User API functions
export const userAPI = {
  getProfile: async () => {
    const response = await api.get('/user/profile');
    return response.data;
  },

  getProfileByEmail: async (email: string) => {
    const response = await api.get(`/users/email/${email}`);
    return response.data;
  },

  updateProfile: async (userData: any) => {
    const response = await api.put('/user/profile', userData);
    return response.data;
  },

  addAddress: async (addressData: any) => {
    const response = await api.post('/user/addresses', addressData);
    return response.data;
  },

  updateAddress: async (addressId: string, addressData: any) => {
    const response = await api.put(`/user/addresses/${addressId}`, addressData);
    return response.data;
  },

  deleteAddress: async (addressId: string) => {
    const response = await api.delete(`/user/addresses/${addressId}`);
    return response.data;
  },
};

// Order API functions
export const orderAPI = {
  createOrder: async (orderData: any) => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },

  getOrder: async (orderId: string) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  getOrders: async (userId: string) => {
    const response = await api.get(`/orders/user/${userId}`);
    return response.data;
  },

  updateOrderStatus: async (orderId: string, statusData: any) => {
    const response = await api.put(`/orders/${orderId}/status`, statusData);
    return response.data;
  },

  cancelOrder: async (orderId: string, reason: string, notes?: string) => {
    const response = await api.post(`/orders/${orderId}/cancel`, { reason, notes });
    return response.data;
  },

};

// Coupon API functions
export const couponAPI = {
  validateCoupon: async (couponCode: string, cartTotal: number) => {
    const response = await api.post('/coupons/validate', { couponCode, cartTotal });
    return response.data;
  },
};

// Payment API functions
export const paymentAPI = {
  initiatePayment: async (paymentData: any) => {
    const response = await api.post('/payment/initiate', paymentData);
    return response.data;
  },

  checkPaymentStatus: async (orderId: string) => {
    const response = await api.get(`/payment/status/${orderId}`);
    return response.data;
  },

  // Layer Payment Gateway methods
  createLayerPaymentToken: async (tokenData: any) => {
    const response = await api.post('/payment/layer/token', tokenData);
    return response.data;
  },

  checkLayerPaymentStatus: async (transactionId: string) => {
    const response = await api.get(`/payment/layer/status/${transactionId}`);
    return response.data;
  },

  handleLayerResponse: async (responseData: any) => {
    const response = await api.post('/payment/layer/response', responseData);
    return response.data;
  },

  // AirPay Payment Gateway methods
  initiateAirpayPayment: async (orderId: string) => {
    const response = await api.post('/payment/airpay/initiate', { orderId });
    return response.data;
  },
};

export default api;
